import React, { forwardRef, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  DrawerNavigation,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import {
  DataRoomRFICategory,
  FileTypeRFI,
  RFI_SEND_REMINDER_TRIGGER,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import useRfiCategory from 'hooks/useRfiCategory';
import useRfiSendCategoriesReminder from 'router/subrouters/DataRoom/hooks/useRfiSendCategoriesReminder';
import { getHasElementsInArray } from 'utils';
import ReminderStatusText from '../../../RfiList/components/RfiRequestsTable/components/ReminderStatusText';
import { getLatestUploadedFile, getSortedCategoryFiles } from '../../utils';
import RfiCategoryDrawerFile from './components/RfiCategoryDrawerFile';

import './RfiCategoryDrawer.styles.scss';

type RfiCategoryDrawerProps = {
  className?: string;
  category?: DataRoomRFICategory;
  isChecklist?: boolean;
  onCategoryUpdate: (category: DataRoomRFICategory) => void;
} & DrawerNavigation;

const RfiCategoryDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  RfiCategoryDrawerProps
> = (
  { className, category, isChecklist, onCategoryUpdate, onPrev, onNext },
  ref,
) => {
  const classes = classNames('rfi-category-drawer', className);

  const { t } = useTranslation();
  const { getCategoryName } = useRfiCategory();

  const { sendCategoriesReminder, loading, hasRecipient } =
    useRfiSendCategoriesReminder(
      category ? [category] : [],
      RFI_SEND_REMINDER_TRIGGER.DOCUMENTS_TABLE,
      onCategoryUpdate,
    );

  const onFileUpdate = useCallback(
    (updatedFile: FileTypeRFI) => {
      onCategoryUpdate({
        ...category,
        files: category.files.map((file) =>
          file._id === updatedFile._id ? updatedFile : file,
        ),
      });
    },
    [category, onCategoryUpdate],
  );

  return (
    <SideDrawer
      className={classes}
      title={getCategoryName(category?.name)}
      ref={ref}
      onPrev={onPrev}
      onNext={onNext}
    >
      <p className="rfi-category-drawer__description">
        {category?.assignedTo || t('requestedFromEveryone')}
      </p>

      {getLatestUploadedFile(category?.files)?.description && (
        <>
          <p className="rfi-category-drawer__label">{t('note')}</p>

          <p className="rfi-category-drawer__description">
            {getLatestUploadedFile(category?.files)?.description}
          </p>
        </>
      )}

      <p className="rfi-category-drawer__label">{t('files')}</p>

      <div className="rfi-category-drawer__files">
        {getHasElementsInArray(category?.files) ? (
          <ul>
            {getSortedCategoryFiles(category.files).map((file) => (
              <RfiCategoryDrawerFile
                key={file._id}
                file={file}
                categoryId={category._id}
                onFileUpdate={onFileUpdate}
              />
            ))}
          </ul>
        ) : (
          <p className="rfi-category-drawer__files__empty">
            {t('nothingUploadedYet')}
          </p>
        )}
      </div>

      <div className="rfi-category-drawer__reminders-header">
        <p className="rfi-category-drawer__label">{t('reminders')}</p>

        {!isChecklist && hasRecipient && (
          <Button
            variant="outline"
            disabled={loading}
            onClick={sendCategoriesReminder}
          >
            {t('remind')}
          </Button>
        )}
      </div>

      {isChecklist ? (
        <p className="rfi-category-drawer__description">
          {t('documentRequestedFromEveryoneReminders')}
        </p>
      ) : (
        <ReminderStatusText
          reminderDates={category?.reminders?.map(({ sentAt }) => sentAt)}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(RfiCategoryDrawer);
